import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/basketball-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "SC", title: "Season schedule", desc: "Build a full schedule across courts, gyms, tip-off times and divisions."},
    {code: "WL", title: "Win-loss standings", desc: "Records and win percentage worked out from every final score."},
    {code: "PD", title: "Points for & against", desc: "Track scoring and point differential to separate teams on equal records."},
    {code: "RS", title: "Result reporting", desc: "Scorers submit the final score and the standings update straight away."},
    {code: "DV", title: "Divisions & conferences", desc: "Run men's, women's, age-group and recreational divisions side by side."},
    {code: "PO", title: "Play-offs", desc: "Take the regular season table into a seeded knockout bracket."},
];

const LEAGUE_TYPES = [
    {name: "Regular Season", sub: "Everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "Season & Play-offs", sub: "Regular season into knockout", to: "/group-and-knockout-generator/"},
    {name: "Basketball Tournaments", sub: "Pools and knockout brackets", to: "/basketball-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Schedule a whole season at once", desc: "Fixtures across gyms, courts and tip-off slots without clashes."},
    {title: "Standings settle themselves", desc: "Records, win percentage and point differential update with each result."},
    {title: "Players follow their own team", desc: "Schedules and standings live on a public page, not in a group chat."},
];

const USE_CASES = [
    {title: "For Rec Leagues", desc: "Run weeknight recreational basketball across several divisions."},
    {title: "For Youth Leagues", desc: "Manage age groups from juniors upwards, each with its own table."},
    {title: "For School & College", desc: "Organise inter-school or intramural competitions across a term."},
    {title: "For 3x3 Leagues", desc: "Run half-court 3x3 competitions alongside the full-court season."},
    {title: "For Corporate Leagues", desc: "Keep workplace basketball running with minimal admin."},
    {title: "For Club Programs", desc: "Track multiple teams from one club across different divisions."},
];

const COMPARE = [
    {need: "Season schedule", manual: "Rebuilt by hand each season", scorepal: "Generated across divisions and game days"},
    {need: "Standings", manual: "Win percentage formulas that break", scorepal: "Records and win percentage calculated automatically"},
    {need: "Tiebreaks", manual: "Point differential worked out manually", scorepal: "Points for, against and differential tracked per team"},
    {need: "Result collection", manual: "Scores texted in and retyped", scorepal: "Submitted against the scheduled game"},
    {need: "Player experience", manual: "Standings shared as a screenshot", scorepal: "A live public page for schedule and standings"},
];

const FAQS = [
    {
        q: 'What is basketball league software?',
        a: <>Basketball league software helps organisers build a season schedule, collect final scores, calculate win-loss standings and publish them for every division without maintaining spreadsheets.</>,
    },
    {
        q: 'Does ScorePal calculate standings automatically?',
        a: <>Yes. Records and win percentage are worked out from the final scores as they are entered, with points for, points against and differential tracked for tiebreaks.</>,
    },
    {
        q: 'Can ScorePal run multiple divisions or conferences?',
        a: <>Yes. Each division, conference or age group runs as its own competition with its own schedule and standings, so a program can run several at once.</>,
    },
    {
        q: 'Can a season finish with play-offs?',
        a: <>Yes. A regular season can feed into a seeded knockout bracket, so the table decides the play-off matchups.</>,
    },
    {
        q: 'Is this different from basketball tournament software?',
        a: <>Yes. Basketball league software is built for a season of recurring games and a standings table. <Link to={"/basketball-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Basketball tournament software</Link> is built for one-day or weekend events with pools and knockout brackets.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Basketball League Software for Clubs & Organisers | ScorePal</title>
            <meta name="description" content="Run basketball leagues with ScorePal. Build season schedules, collect scores, track win-loss records, point differential and publish standings for every division." />
            <link rel="canonical" href={`${config.publicUrl}/basketball-league-software/`} />
            <meta property="og:title" content="Basketball League Software | ScorePal" />
            <meta property="og:description" content="Create basketball season schedules, collect scores and publish automatic standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/basketball-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/basketball-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Basketball League Software | ScorePal" />
            <meta name="twitter:description" content="Run basketball season schedules, scores and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Basketball League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Basketball league software for building season schedules, collecting scores, tracking win-loss records and publishing standings.",
                "url": "https://scorepal.co/basketball-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is basketball league software?", "acceptedAnswer": {"@type": "Answer", "text": "Basketball league software helps organisers build a season schedule, collect final scores, calculate win-loss standings and publish them for every division without maintaining spreadsheets."}},
                    {"@type": "Question", "name": "Does ScorePal calculate standings automatically?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Records and win percentage are worked out from the final scores as they are entered, with points for, points against and differential tracked for tiebreaks."}},
                    {"@type": "Question", "name": "Can ScorePal run multiple divisions or conferences?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Each division, conference or age group runs as its own competition with its own schedule and standings, so a program can run several at once."}},
                    {"@type": "Question", "name": "Can a season finish with play-offs?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. A regular season can feed into a seeded knockout bracket, so the table decides the play-off matchups."}},
                    {"@type": "Question", "name": "Is this different from basketball tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Basketball league software is built for a season of recurring games and a standings table, while basketball tournament software is built for one-day or weekend events with pools and knockout brackets."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Basketball League Software", item: "https://scorepal.co/basketball-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Basketball League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run a basketball season without rebuilding the standings
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps basketball organisers schedule a full season across gyms and courts,
                                    collect final scores, and publish win-loss standings with point differential — for
                                    every division you run.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/basketball-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live standings card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live basketball standings</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Records and differential update the moment a final score is submitted.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_48px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Team</span><span className="text-center">W-L</span><span className="text-right">Diff</span>
                                    </div>
                                    {[
                                        {pos: 1, team: "Downtown Hoops", rec: "9-1", diff: "+112"},
                                        {pos: 2, team: "Westside Ballers", rec: "7-3", diff: "+54"},
                                        {pos: 3, team: "Court Kings", rec: "5-5", diff: "-8"},
                                        {pos: 4, team: "Northgate Nets", rec: "2-8", diff: "-96"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_48px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.rec}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.diff}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run a basketball league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace the schedule spreadsheet, the standings you retype every week and the scores that arrive by text.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of basketball competition
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        A straight regular season, a season that finishes in play-offs, or a weekend tournament — ScorePal handles the structure.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Season + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Built for the rhythm of a basketball season
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Teams want to know when they tip off and which court they are on. Organisers want scores in
                            without chasing, and a table that is right before the next game night comes round.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Division A — Week 7", r: "In progress", badge: "live"},
                                {l: "Next game", r: "Court Kings vs Westside Ballers"},
                                {l: "Tip-off", r: "Thursday 19:30 · Court 1"},
                                {l: "Results in", r: "38 / 55 games"},
                                {l: "Top of the table", r: "Downtown Hoops", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for basketball leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        A schedule spreadsheet copes until a game is rescheduled, a division is added or two teams finish level — then it needs babysitting.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for rec leagues, youth programs and school basketball
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From a weeknight rec league to a multi-division youth program, ScorePal keeps schedules and standings in order.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a weekend basketball tournament?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about league seasons. If you are running pool play into knockout brackets across
                            a single day or weekend, see our basketball tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/basketball-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Pool play</li>
                            <li>Knockout brackets</li>
                            <li>Consolation rounds</li>
                            <li>Age divisions</li>
                            <li>Court scheduling</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next basketball league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Season schedules, final scores, win-loss records, point differential and play-offs — basketball league software built for modern organisers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Season Schedule", "Win-Loss Records", "Point Differential", "Divisions", "Play-offs", "Live Standings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Basketball League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
